import React, { useEffect, useRef, useState } from "react";
import { money } from "@/lib/api";

export const CANVAS_W = 1000;
export const CANVAS_H = 620;
const SNAP = 10;

export const STATUS_COLORS = {
  AVAILABLE: { bg: "#ecfdf5", border: "#10b981", label: "Свободна" },
  RESERVED: { bg: "#eff6ff", border: "#3b82f6", label: "Резервирана" },
  OCCUPIED: { bg: "#fffbeb", border: "#f59e0b", label: "Заета" },
};
export const tableState = (t) => (t.order_id ? "OCCUPIED" : t.status === "RESERVED" ? "RESERVED" : "AVAILABLE");

// Tables never placed yet (0,0) get a tidy grid position.
export const withAutoPos = (tables) => tables.map((t, i) => (t.pos_x || t.pos_y ? t : { ...t, pos_x: 30 + (i % 8) * 120, pos_y: 30 + Math.floor(i / 8) * 130 }));

function useScale(ref) {
  const [scale, setScale] = useState(1);
  useEffect(() => {
    if (!ref.current) return undefined;
    const ro = new ResizeObserver(([e]) => setScale(Math.min(1, e.contentRect.width / CANVAS_W)));
    ro.observe(ref.current);
    return () => ro.disconnect();
  }, [ref]);
  return scale;
}

function TableShape({ t, selected, editable, onPointerDown, onClick, onResizeDown }) {
  const st = STATUS_COLORS[tableState(t)];
  const w = t.w || 90, h = t.shape === "round" || t.shape === "square" ? (t.w || 90) : (t.h || 60);
  return (
    <div onPointerDown={onPointerDown} onClick={onClick} data-testid={`floor-table-${t.number}`}
      className={`absolute flex flex-col items-center justify-center text-center select-none shadow-sm transition-shadow ${editable ? "cursor-move" : "cursor-pointer hover:shadow-lg"}`}
      style={{ left: t.pos_x, top: t.pos_y, width: w, height: h, background: st.bg, border: `3px solid ${selected ? "#C5A059" : st.border}`,
        borderRadius: t.shape === "round" ? "50%" : 12, boxShadow: selected ? "0 0 0 4px rgba(197,160,89,.35)" : undefined, touchAction: "none" }}>
      <div className="font-bold text-forest-deep leading-none" style={{ fontSize: 18 }}>{t.name || t.number}</div>
      <div className="text-[11px] text-forest/70">{t.seats} м.</div>
      {t.current_total > 0 && <div className="text-[11px] font-bold text-forest-deep">{money(t.current_total)}</div>}
      {t.waitress_name && <div className="text-[10px] text-gold-dark truncate max-w-full px-1">{t.waitress_name}</div>}
      {editable && selected && (
        <div onPointerDown={onResizeDown} className="absolute -right-2 -bottom-2 w-5 h-5 bg-gold rounded-md cursor-se-resize" data-testid="floor-resize-handle" />
      )}
    </div>
  );
}

export function FloorCanvas({ tables, editable, selectedId, onSelect, onChange, onOpen, onDragStart }) {
  const wrap = useRef(null);
  const scale = useScale(wrap);
  const drag = useRef(null);

  const start = (e, t, mode) => {
    if (!editable) return;
    e.stopPropagation();
    onSelect?.(t.id);
    onDragStart?.();
    drag.current = { id: t.id, mode, sx: e.clientX, sy: e.clientY, ox: t.pos_x, oy: t.pos_y, ow: t.w || 90, oh: t.h || 60 };
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };
  const move = (e) => {
    const d = drag.current;
    if (!d) return;
    const dx = (e.clientX - d.sx) / scale, dy = (e.clientY - d.sy) / scale;
    const snap = (v) => Math.round(v / SNAP) * SNAP;
    if (d.mode === "move") onChange(d.id, { pos_x: Math.max(0, Math.min(CANVAS_W - 40, snap(d.ox + dx))), pos_y: Math.max(0, Math.min(CANVAS_H - 40, snap(d.oy + dy))) });
    else onChange(d.id, { w: Math.max(50, snap(d.ow + dx)), h: Math.max(40, snap(d.oh + dy)) });
  };
  const end = () => { drag.current = null; };

  return (
    <div ref={wrap} className="w-full" style={{ height: CANVAS_H * scale }}>
      <div onPointerMove={move} onPointerUp={end} onPointerLeave={end} onClick={() => editable && onSelect?.(null)}
        className="relative rounded-2xl border-2 border-dashed border-gold/30 bg-[radial-gradient(circle,#d6cfbf_1px,transparent_1px)] [background-size:20px_20px] bg-cream origin-top-left"
        style={{ width: CANVAS_W, height: CANVAS_H, transform: `scale(${scale})` }} data-testid="floor-canvas">
        {tables.map((t) => (
          <TableShape key={t.id} t={t} editable={editable} selected={selectedId === t.id}
            onPointerDown={(e) => start(e, t, "move")} onResizeDown={(e) => start(e, t, "resize")}
            onClick={(e) => { e.stopPropagation(); if (!editable) onOpen?.(t); }} />
        ))}
      </div>
    </div>
  );
}

export const StatusLegend = () => (
  <div className="flex flex-wrap gap-3 text-xs">
    {Object.entries(STATUS_COLORS).map(([k, s]) => (
      <span key={k} className="flex items-center gap-1.5"><span className="w-3 h-3 rounded" style={{ background: s.bg, border: `2px solid ${s.border}` }} />{s.label}</span>
    ))}
  </div>
);
